import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import AdminLayout from '../layouts/AdminLayout';
import PermissionRoute from '../components/common/PermissionRoute';
import Unauthorized from '../pages/error/Unauthorized';

const AllLiveSession = lazy(() => import('../pages/admin/livesession/AllLiveSession'));
const AllCourses = lazy(() => import('../pages/admin/courses/AllCourses'));
const ApprovalRequest = lazy(() => import('../pages/admin/approvalRequest/ApprovalRequest'));
const LiveSessionAssessments = lazy(() => import('../pages/admin/livesession/LiveSessionAssessments'));
const LiveSessionAssignments = lazy(() => import('../pages/admin/livesession/LiveSessionAssignments'));

const LiveSessionDashboard = lazy(() => import('../pages/admin/livesession/LiveSessionDashboard'));
const LiveSessionLiveClasses = lazy(() => import('../pages/admin/livesession/LiveSessionLiveClasses'));
const LiveSessionTrainees = lazy(() => import('../pages/admin/livesession/LiveSessionTrainees'));
const LiveSessionAssessmentDetails = lazy(() => import('../pages/admin/livesession/LiveSessionAssessmentDetails'));
const LiveSessionAssignmentDetails = lazy(() => import('../pages/admin/livesession/LiveSessionAssignmentDetails'));
const QuestionBank = lazy(() => import('../pages/admin/questionBank/QuestionBank'));
const AdminBillingDashboard = lazy(() => import('../pages/admin/billing/AdminBillingDashboard'));
const ManageSubscriptions = lazy(() => import('../pages/admin/billing/ManageSubscriptions'));
const AllCertificates = lazy(() => import('../pages/admin/certificates/AllCertificates'));
const Announcement = lazy(() => import('../pages/admin/announcement/announcement'));
const RolesAndAccess = lazy(() => import('../pages/admin/roles_and_access/RolesAndAccess'));
const CourseAnalytics = lazy(() => import('../pages/admin/courses/CourseAnalytics'));
const CourseAnalyticsAssessmentDetails = lazy(() => import('../pages/admin/courses/CourseAnalyticsAssessmentDetails'));
const CourseAnalyticsSurveyDetails = lazy(() => import('../pages/admin/courses/CourseAnalyticsSurveyDetails'));
const CourseDocument = lazy(() => import('../pages/admin/courses/CourseDocument'));
const CourseModule = lazy(() => import('../pages/admin/courses/CourseModule'));
const CourseQuiz = lazy(() => import('../pages/admin/courses/CourseQuiz'));
const CourseSurvey = lazy(() => import('../pages/admin/courses/CourseSurvey'));
const CourseVideo = lazy(() => import('../pages/admin/courses/CourseVideo'));
const Courses = lazy(() => import('../pages/admin/courses/Courses'));
const CreateCourse = lazy(() => import('../pages/admin/courses/CreateCourse'));
const Dashboard = lazy(() => import('../pages/admin/dashboard/Dashboard'));
const ModuleContent = lazy(() => import('../pages/admin/courses/ModuleContent'));
const NotificationDetails = lazy(() => import('../pages/admin/Notifications/NotificationDetails'));
const Notifications = lazy(() => import('../pages/admin/Notifications/Notifications'));
const Profile = lazy(() => import('../pages/admin/profile/Profile'));
const Settings = lazy(() => import('../pages/admin/settings/Settings'));
const TraineeAnalytics = lazy(() => import('../pages/admin/trainees/TraineeAnalytics'));
const VideoPlayer = lazy(() => import('../pages/admin/courses/CourseVideo/VideoPlayer'));
const PromptAssistant = lazy(() => import('../pages/admin/PromptAssistant/PromptAssistant'));
const AdminConsoleCourseModule = lazy(() => import('../pages/admin/approvalRequest/AdminConsoleCourseModule'));
const ZoomMeeting = lazy(() => import('../components/zoom/ZoomMeeting'));
const ContentBlogs = lazy(() => import('../pages/admin/content/Blogs'));
const ContentPages = lazy(() => import('../pages/admin/content/Pages'));
const ContentCategories = lazy(() => import('../pages/admin/content/Categories'));
const CourseCategories = lazy(() => import('../pages/admin/courses/CourseCategories'));
const SupportLeads = lazy(() => import('../pages/admin/support/SupportLeads'));
const SupportTickets = lazy(() => import('../pages/admin/support/SupportTickets'));
const SupportFAQ = lazy(() => import('../pages/admin/support/FAQ'));
const Support = lazy(() => import('../pages/admin/support/Support'));

const Fallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function AdminRoutes() {
  return (
    <>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/zoom-meeting" element={<ZoomMeeting />} />

          <Route path="/" element={<AdminLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="profile"
              element={
                <PermissionRoute permissionKey="profile_management_view">
                  <Profile />
                </PermissionRoute>
              }
            />

            <Route
              path="courses"
              element={
                <PermissionRoute permissionKey="course_management_view">
                  <Courses />
                </PermissionRoute>
              }
            />
            <Route
              path="AllCourses"
              element={
                <PermissionRoute permissionKey="course_management_view">
                  <AllCourses />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId"
              element={
                <PermissionRoute permissionKey="course_module_management_view">
                  <CourseModule />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId/content/:moduleId"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <ModuleContent />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId/content/:moduleId/document/:contentId"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseDocument />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId/content/:moduleId/survey/:contentId"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseSurvey />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId/content/:moduleId/quiz/:contentId"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseQuiz />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/module/:courseId/content/:moduleId/video/:contentId"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <VideoPlayer />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/create"
              element={
                <PermissionRoute permissionKey="course_management_view">
                  <CreateCourse />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/edit-create/:courseId"
              element={
                <PermissionRoute permissionKey="course_management_view">
                  <CreateCourse />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/analytics/:courseId"
              element={
                <PermissionRoute permissionKey="course_management_view_analytics">
                  <CourseAnalytics />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/analytics/:courseId/CourseAnalyticsAssessmentDetails/:assessmentId"
              element={
                <PermissionRoute permissionKey="course_management_view_analytics">
                  <CourseAnalyticsAssessmentDetails />
                </PermissionRoute>
              }
            />
            <Route
              path="courses/analytics/:courseId/CourseAnalyticsSurveyDetails/:surveyId"
              element={
                <PermissionRoute permissionKey="course_management_view_analytics">
                  <CourseAnalyticsSurveyDetails />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseModule"
              element={
                <PermissionRoute permissionKey="course_module_management_view">
                  <CourseModule />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseModuleContent"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <ModuleContent />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseVideo"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseVideo />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseDocument"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseDocument />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseQuiz"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseQuiz />
                </PermissionRoute>
              }
            />
            <Route
              path="CourseSurvey"
              element={
                <PermissionRoute permissionKey="course_module_content_management_view">
                  <CourseSurvey />
                </PermissionRoute>
              }
            />

            <Route
              path="roles-and-access"
              element={
                <PermissionRoute permissionKey="roles_and_access_user_view">
                  <RolesAndAccess />
                </PermissionRoute>
              }
            />
            <Route path="trainees" element={<Navigate to="/admin/roles-and-access" replace />} />
            <Route
              path="traineesAnalytics/:traineeId"
              element={
                <PermissionRoute permissionKey="trainee_management_view_analytics">
                  <TraineeAnalytics />
                </PermissionRoute>
              }
            />
            <Route
              path="admin-console"
              element={
                <PermissionRoute permissionKey="course_approval_workflow_view">
                  <ApprovalRequest />
                </PermissionRoute>
              }
            />
            <Route
              path="admin-console/module/:courseId"
              element={
                <PermissionRoute permissionKey="course_approval_workflow_view">
                  <AdminConsoleCourseModule />
                </PermissionRoute>
              }
            />

            <Route
              path="livesessions"
              element={
                <PermissionRoute permissionKey="livesession_management_view">
                  <AllLiveSession />
                </PermissionRoute>
              }
            />
            <Route
              path="livesessions/livesession-dashboard/:livesessionId"
              element={
                <PermissionRoute permissionKey="livesession_management_view">
                  <LiveSessionDashboard />
                </PermissionRoute>
              }
            />
            <Route
              path="livesessions/livesession-dashboard/:livesessionId/trainees/:livesessionId"
              element={
                <PermissionRoute permissionKey="livesession_trainees_view">
                  <LiveSessionTrainees />
                </PermissionRoute>
              }
            />
            <Route
              path="LiveSessionAssignments"
              element={
                <PermissionRoute permissionKey="livesession_assignment_view">
                  <LiveSessionAssignments />
                </PermissionRoute>
              }
            />
            <Route
              path="livesessions/livesession-dashboard/:livesessionId/assingment/:assignmentId/LiveSessionAssignmentDetails"
              element={
                <PermissionRoute permissionKey="livesession_assignment_view">
                  <LiveSessionAssignmentDetails />
                </PermissionRoute>
              }
            />
            <Route
              path="LiveSessionAssessments"
              element={
                <PermissionRoute permissionKey="livesession_assessment_view">
                  <LiveSessionAssessments />
                </PermissionRoute>
              }
            />
            <Route
              path="livesessions/livesession-dashboard/:livesessionId/assessment/:assessmentId/LiveSessionAssessmentDetails"
              element={
                <PermissionRoute permissionKey="livesession_assessment_view">
                  <LiveSessionAssessmentDetails />
                </PermissionRoute>
              }
            />
            <Route
              path="LiveSessionLiveClasses"
              element={
                <PermissionRoute permissionKey="livesession_live_classes_view">
                  <LiveSessionLiveClasses />
                </PermissionRoute>
              }
            />

            <Route
              path="question-bank"
              element={
                <PermissionRoute permissionKey="question_bank_management_view">
                  <QuestionBank />
                </PermissionRoute>
              }
            />

            <Route
              path="certificates"
              element={
                <PermissionRoute permissionKey="certificate_management_view">
                  <AllCertificates />
                </PermissionRoute>
              }
            />
            <Route
              path="announcement"
              element={
                <PermissionRoute permissionKey="announcement_management_view">
                  <Announcement />
                </PermissionRoute>
              }
            />

            <Route
              path="billing"
              element={
                <PermissionRoute permissionKey="billing_management_view">
                  <AdminBillingDashboard />
                </PermissionRoute>
              }
            />
            <Route
              path="billing/manage-subscriptions"
              element={
                <PermissionRoute permissionKey="billing_management_view">
                  <ManageSubscriptions />
                </PermissionRoute>
              }
            />

            <Route
              path="notifications"
              element={
                <PermissionRoute permissionKey="notification_management_view">
                  <Notifications />
                </PermissionRoute>
              }
            />
            <Route
              path="notifications/:id"
              element={
                <PermissionRoute permissionKey="notification_management_view">
                  <NotificationDetails />
                </PermissionRoute>
              }
            />

            <Route
              path="settings"
              element={
                <PermissionRoute permissionKey="setting_management_view">
                  <Settings />
                </PermissionRoute>
              }
            />
            <Route path="prompt-assistant" element={<PromptAssistant />} />

            <Route
              path="content/blogs"
              element={
                <PermissionRoute permissionKey="content_management_blogs_view">
                  <ContentBlogs />
                </PermissionRoute>
              }
            />
            <Route
              path="content/pages"
              element={
                <PermissionRoute permissionKey="content_management_pages_view">
                  <ContentPages />
                </PermissionRoute>
              }
            />
            <Route
              path="content/categories"
              element={
                <PermissionRoute permissionKey="content_management_blog_categories_view">
                  <ContentCategories />
                </PermissionRoute>
              }
            />
            <Route
              path="categories"
              element={
                <PermissionRoute permissionKey="course_management_categories_view">
                  <CourseCategories />
                </PermissionRoute>
              }
            />
            <Route
              path="support"
              element={
                <PermissionRoute permissionKey="support_contact_leads_view">
                  <Support />
                </PermissionRoute>
              }
            />
            <Route
              path="support/leads"
              element={
                <PermissionRoute permissionKey="support_contact_leads_view">
                  <SupportLeads />
                </PermissionRoute>
              }
            />
            <Route
              path="support/tickets"
              element={
                <PermissionRoute permissionKey="support_tickets_view">
                  <SupportTickets />
                </PermissionRoute>
              }
            />
            <Route
              path="support/faq"
              element={
                <PermissionRoute permissionKey="support_faq_view">
                  <SupportFAQ />
                </PermissionRoute>
              }
            />
          </Route>
          <Route path="unauthorized" element={<Unauthorized />} />
        </Routes>
      </Suspense>
      <ToastContainer limit={3} />
    </>
  );
}

export default AdminRoutes;
